import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

import OrganizationSelect from "../organization/OrganizationSelect";
import Loading from "../../layout/Loading";
import Button from "../../core/Button";
import Form from "../../layout/Form";
import EditAvatar from "../../forms/EditAvatar";
import UserRoleSelect from "./UserRoleSelect";
import FormFieldText from "../../input/FormFieldText";
import FormFieldTags from "../../input/FormFieldTags";
import FormFieldPassword from "../../input/FormFieldPassword";
import FormFieldSelect from "../../input/FormFieldSelect";
import api from "../../../services/api";

import useTriggeredAPICall from "../../../hooks/fetch-hooks/useTriggeredAPICall";
import useAPICall from "../../../hooks/fetch-hooks/useAPICall";
import { useAuthInfo } from "../../../context/AuthContext";

import { errorToast, successfulToast } from "../../../util/toastNotifications";
import {
  isValidEmail,
  isValidPassword,
  isValidPhoneNumber,
} from "../../../util/stringUtils";

const INITIAL_USER = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  phone: "",
  orgId: "",
  roleId: "",
  userType: "User",
  is_active: true,
  is_login_enabled: true,
  tags: [],
};

const UserForm = (props) => {
  const userId = props.match.params.user_id;
  const location = useLocation();

  const [isContact, setIsContact] = useState(
    location.pathname === "/contact-add"
  );
  const [organization, setOrganization] = useState("");
  const [formFields, setFormFields] = useState(INITIAL_USER);
  const [formErrors, setFormErrors] = useState({});
  const [isInitialLoading, setIsInitialLoading] = useState(false);

  const { userInfo, updateUserInfo } = useAuthInfo();
  const { data: user, fetchData, isLoading } = useTriggeredAPICall();
  const isSuperAdmin = userInfo?.role?.name === "super-admin";
  const shouldFetchOrgs = !isContact || isSuperAdmin;
  const canManageOrgAndRole = userInfo.role.name !== "user";
  const noChangeOwnRole = user?.results.user_id !== userInfo.user_id;

  const { data: roleData } = useAPICall(
    "/roles",
    "GET",
    null,
    {},
    canManageOrgAndRole && !isContact
  );
  const { data: orgsData, isLoading: isOrgsLoading } = useAPICall(
    "/organizations",
    "GET",
    null,
    {},
    shouldFetchOrgs
  );
  const params = new URLSearchParams(location.search);
  const companyId = params.get("company_id");

  useEffect(() => {
    if (!companyId) return;
    const loadCompany = async () => {
      const companyData = await api.company.getCompanyById(companyId);
      setOrganization(companyData.organization);
      setFormFields((prev) => ({
        ...prev,
        orgId: companyData.organization.org_id,
      }));
    };
    loadCompany();
  }, [api, companyId]);

  const isOrganizationBlocked = Boolean(companyId);

  const currentOrgData = useMemo(() => {
    if (orgsData && formFields.orgId) {
      const orgResults = orgsData.results;
      const orgData = orgResults.find((org) => org.org_id === formFields.orgId);

      return orgData;
    }
  }, [orgsData, formFields.orgId]);

  const validateForm = () => {
    const errors = {};

    if (!formFields.firstName.trim()) {
      errors["firstName"] = "First Name is a required field";
    }

    if (!formFields.lastName.trim()) {
      errors["lastName"] = "Last Name is a required field";
    }

    if (!isValidPhoneNumber(formFields.phone) && formFields.phone.length > 0) {
      errors["phone"] = "Invalid Phone, only valid phone numbers will be saved";
    }

    if (!isValidEmail(formFields.email)) {
      errors["email"] = "Email is a required field";
    }

    const resolvedOrgId =
      formFields.orgId || userInfo?.organization?.org_id || userInfo?.org_id;
    if (!resolvedOrgId) {
      errors["orgId"] = "Organization is a required field";
    }

    if (canManageOrgAndRole && !isContact && !formFields.roleId) {
      errors["roleId"] = "Role is a required field";
    }

    if (!userId) {
      if (
        formFields.userType === "User" &&
        !isValidPassword(formFields.password)
      ) {
        errors["password"] =
          "* Password must be between 8-20 characters with one special character, one number, and one capital letter.";
      }
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };

  const handleFieldUpdate = (e) => {
    const { name, value } = e.target;

    if (name === "roleId" && formFields.userType === "Contact") {
      return;
    }

    setFormFields((previous) => {
      if (name === "orgId") {
        if (isContact || previous.userType === "Contact") {
          return { ...previous, orgId: value };
        }
        const userRole = roleData?.results?.find(
          (role) => role.name === "user"
        );

        return { ...previous, orgId: value, roleId: userRole?.role_id };
      } else if (name === "userType") {
        const updatedFields = { ...previous, userType: value };
        updatedFields.is_login_enabled = value === "User";
        if (value === "Contact") {
          updatedFields.password = "";
          const noneRole = roleData?.results?.find(
            (role) => role.name === "none"
          );
          if (noneRole) {
            updatedFields.roleId = noneRole.role_id;
          }
        } else if (value === "User") {
          const userRole = roleData?.results?.find(
            (role) => role.name === "user"
          );
          if (userRole) {
            updatedFields.roleId = userRole.role_id;
          }
        }
        return updatedFields;
      } else {
        return { ...previous, [name]: value };
      }
    });

    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formValidation = validateForm();

    if (formValidation) {
      setFormErrors(formValidation);
      return;
    }

    const isUser = formFields.userType === "User";
    let fetchUrl = isContact && !userId ? "/contact" : "/user";
    let method = "POST";

    const body = {
      first_name: formFields.firstName,
      last_name: formFields.lastName,
      email: formFields.email,
      phone: formFields.phone,
      org_id:
        formFields.orgId || userInfo?.organization?.org_id || userInfo?.org_id,
      is_active: formFields.is_active,
      is_login_enabled: isUser,
    };

    if (!isContact && formFields.roleId) {
      body.role_id = formFields.roleId;
    }

    if (userId) {
      fetchUrl = `/user/${userId}`;
      method = "PUT";
      if (formFields.roleId) {
        body.role_id = formFields.roleId;
      }
    } else if (isUser) {
      body.password = formFields.password;
      body.is_verified_email = true;
    } else {
      body.is_verified_email = false;
    }

    await fetchData(fetchUrl, method, body)
      .then((result) => {
        if (!result) return;

        if (userId === userInfo?.user_id && result?.results) {
          updateUserInfo((previous) => ({ ...previous, ...result.results }));
        }
        return result;
      })
      .then(async (result) => {
        if (!result) return;

        if (!userId) {
          const newUserId = result?.results?.user_id;

          if (newUserId && formFields.tags && formFields.tags.length > 0) {
            try {
              await Promise.all(
                formFields.tags.map((tag) =>
                  fetchData("/user/tag", "POST", {
                    user_id: newUserId,
                    tag_id: tag.tag_id,
                  })
                )
              );
            } catch (err) {
              console.error("Error adding tags to user:", err);
            }
          }

          if (isUser) {
            successfulToast("Sent verification email");
          } else {
            successfulToast("Contact created successfully");
          }
          const newContactId = result.results.user_id;

          if (companyId) {
            await fetchData(`/company/${companyId}/contacts`, "PUT", {
              contact_id: newContactId,
              role: "Contact",
            });
          }

          let destination = "/users";
          if (isContact && !isOrganizationBlocked) {
            destination = "/contacts";
          } else if (isContact && isOrganizationBlocked) {
            destination = `company/${companyId}`;
          } else if (newUserId) {
            destination = `/user/${newUserId}`;
          }
          props.history.push(destination);
        } else {
          props.history.push(`/user/${userId}`);
        }
      })
      .catch((err) => {
        let message =
          "Error submitting user, please check all fields and try again";

        if (err.status === 409) {
          message =
            err.message && err.message !== `Request failed (${err.status})`
              ? err.message
              : "A user with this email already exists";
        } else if (err.status === 403 && err.message) {
          message = err.message;
        } else if (err.message && err.message !== `Request failed (${err.status})`) {
          message = err.message;
        } else if (err.status) {
          message = `Unable to save user (error ${err.status}). Please try again or contact support.`;
        }

        errorToast(message);
      });
  };

  const addTagToUser = (tagId) => {
    if (!userId) {
      const tagExists = formFields.tags.some((tag) => tag.tag_id === tagId);
      if (tagExists) {
        return;
      }

      fetchData(`/tag/${tagId}`, "GET")
        .then((result) => {
          const newTag = {
            tag_id: result.results.tag_id,
            name: result.results.name,
            color: result.results.color,
          };
          setFormFields((prev) => ({
            ...prev,
            tags: [...(prev.tags || []), newTag],
          }));
        })
        .catch((err) => console.error("Add Tag to User", err));
      return;
    }

    const body = {
      user_id: userId,
      tag_id: tagId,
    };

    fetchData("/user/tag", "POST", body)
      .then((result) => {
        setFormFields((prev) => ({ ...prev, tags: result.results.tags }));
      })
      .catch((err) => console.error("Add Tag to User", err));
  };

  const handleTagRemove = (tagId) => {
    if (!userId) {
      setFormFields((prev) => ({
        ...prev,
        tags: (prev.tags || []).filter((tag) => tag.tag_id !== tagId),
      }));
      return;
    }

    const body = {
      user_id: userId,
      tag_id: tagId,
    };

    fetchData("/user/tag", "POST", body).then((result) =>
      setFormFields((prev) => ({ ...prev, tags: result.results.tags }))
    );
  };

  useEffect(() => {
    if (!userId) {
      setFormFields((previous) => ({
        ...previous,
        userType: isContact ? "Contact" : previous.userType || "User",
      }));
      return;
    }

    setIsInitialLoading(true);

    fetchData(`/user/${userId}`, "GET")
      .then((result) => {
        if (!result?.results) return;

        // Older records may have is_login_enabled=false even when they are
        // real users (admin/user/super-admin). Contacts use role "none".
        const roleName = result.results.role?.name;
        const isLoginUser =
          result.results.is_login_enabled === true ||
          (Boolean(roleName) && roleName !== "none");

        setFormFields({
          firstName: result.results.first_name,
          lastName: result.results.last_name,
          email: result.results.email,
          phone: result.results.phone,
          orgId: result.results.organization.org_id,
          roleId: result.results.role_id || "",
          userType: isLoginUser ? "User" : "Contact",
          is_active: result.results.is_active,
          is_login_enabled: isLoginUser,
          tags: result.results.tags,
        });
        setIsContact(!isLoginUser);
      })
      .catch((err) => console.error("Load User", err))
      .finally(() => setIsInitialLoading(false));
  }, [userId, fetchData]);

  useEffect(() => {
    if (userId) return;

    if (!userId && isContact && !isSuperAdmin) {
      setFormFields((previous) => ({
        ...previous,
        userType: "Contact",
        orgId: userInfo?.organization?.org_id || userInfo?.org_id || "",
      }));
    } else if (orgsData && !isSuperAdmin && !userId) {
      const orgResults = orgsData.results;
      const userOrgId = userInfo.organization.org_id;

      const organization =
        orgResults.find((org) => org.org_id === userOrgId) ||
        userInfo.organization;
      const userRole = roleData?.results?.find((role) => role.name === "user");

      setFormFields((previous) => ({
        ...previous,
        orgId: organization?.org_id || userOrgId,
        roleId: isContact ? previous.roleId : userRole?.role_id,
        userType: previous.userType || "User",
      }));
    }
  }, [
    userId,
    user,
    isSuperAdmin,
    isContact,
    userInfo.role.name,
    userInfo.organization,
    userInfo.org_id,
    orgsData,
    roleData?.results,
  ]);

  useEffect(() => {
    if (formFields.userType === "Contact" && roleData?.results) {
      const noneRole = roleData.results.find((role) => role.name === "none");
      if (noneRole && formFields.roleId !== noneRole.role_id) {
        setFormFields((previous) => ({
          ...previous,
          roleId: noneRole.role_id,
        }));
      }
    }
  }, [formFields.userType, formFields.roleId, roleData?.results]);

  if (userId && (isInitialLoading || (isOrgsLoading && canManageOrgAndRole))) {
    return <Loading />;
  }

  return (
    <div className="form-styling px-16 py-8 card">
      <h2>
        {userId ? "Edit" : "Add"}
        {isContact ? " Contact" : " User"}
      </h2>

      <Form onSubmit={handleSubmit}>
        {!userId && (
          <FormFieldSelect
            name="userType"
            label="Type *"
            placeholder="Select Type..."
            value={isContact ? "Contact" : formFields.userType || "User"}
            onChange={handleFieldUpdate}
            disabled={isContact}
            renderOptions={() => (
              <>
                <option value="User">User</option>
                <option value="Contact">Contact</option>
              </>
            )}
            errorMessage={formErrors.userType}
          />
        )}

        {canManageOrgAndRole && (
          <OrganizationSelect
            orgId={formFields.orgId}
            orgsData={orgsData}
            handleFieldUpdate={handleFieldUpdate}
            errorMessage={formErrors.orgId}
            disabled={isOrganizationBlocked}
          />
        )}
        {canManageOrgAndRole && !isContact && noChangeOwnRole && (
          <UserRoleSelect
            roleId={formFields.roleId}
            roles={roleData?.results}
            userType={formFields.userType}
            handleFieldUpdate={handleFieldUpdate}
            errorMessage={formErrors.roleId}
          />
        )}

        <FormFieldText
          name="firstName"
          label="First Name"
          placeholder="Enter first name..."
          autoComplete="off"
          maxLength="45"
          value={formFields.firstName}
          onChange={handleFieldUpdate}
          errorMessage={formErrors.lastName}
        ></FormFieldText>

        <FormFieldText
          name="lastName"
          label="Last Name"
          placeholder="Enter last name..."
          maxLength="45"
          value={formFields.lastName}
          onChange={handleFieldUpdate}
          errorMessage={formErrors.lastName}
        ></FormFieldText>

        <FormFieldText
          name="email"
          label="Email *"
          placeholder="Enter email address..."
          value={formFields.email}
          errorMessage={formErrors.email}
          onChange={handleFieldUpdate}
        ></FormFieldText>

        <FormFieldText
          name="phone"
          label="Phone"
          placeholder="Enter the phone number..."
          type="text"
          maxLength="45"
          value={formFields.phone}
          errorMessage={formErrors.phone}
          onChange={handleFieldUpdate}
        ></FormFieldText>

        {!userId && formFields.userType === "User" && !isContact && (
          <FormFieldPassword
            name="password"
            label="Password *"
            placeholder="Enter your password..."
            value={formFields.password}
            onChange={handleFieldUpdate}
            errorMessage={formErrors.password}
          />
        )}

        {userId && (
          <FormFieldTags
            selectedTags={formFields.tags}
            handleTagRemove={handleTagRemove}
            objectType="users"
            objectId={userId}
            objectPk="user_id"
            handleAddTagToObject={addTagToUser}
          />
        )}

        <div className="button-row">
          <div className="button-row-left">
            <Button cancel>Cancel</Button>
          </div>

          <div className="button-row-right">
            <button type="submit" disabled={isLoading}>
              {isLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : userId ? (
                "Save"
              ) : !isContact ? (
                "Add User"
              ) : (
                "Add Contact"
              )}
            </button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default UserForm;
